import {useCallback, useEffect} from 'react';
import {useSelector} from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';

import utils from '../helpers/utils';
import _get from 'lodash/get';
import {RootState} from '../reducers/rootReducer';
import usePrevious from './usePrevious';
import {useLocation} from 'react-router-dom';

/**
 * Custom hook to handle analytics related functionalities
 * @returns Analytics related methods
 */
/**
 *
 * This hook manages analytics tracking for different SPA experiences
 * (Home Title, ID Advisor, Dark Web, ITPS) and sets up necessary analytics
 * data for both internal tracking and third-party services like Qualtrics.
 *
 * The hook tracks various user activities including:
 * - Page navigation changes
 * - Modal openings/closings
 * - Alert notifications
 * - User profile updates
 *
 * @remarks
 * This hook relies on the global `window.nortonAnalytics` and `window.analytics`
 * objects which it populates with user, plan, and navigation data.
 * It also communicates with a parent application via the `sendMessage` utility.
 *
 * @returns {Object} An object containing analytics utility functions
 * @returns {Function} returns.getSpaExperienceForAnalytics - Returns the current SPA experience type string
 * @returns {Function} returns.setupAnalytics - Sets up analytics data with current user information
 *
 */
/**
 * Monitors changes to the user object and sends updated plan information to the parent window.
 * When the user data changes, this effect extracts the plan name and forwards it to the parent
 * application via the SET_ITPS_DATA_BAG message. This ensures that the parent application
 * has the most current plan information for analytics purposes.
 *
 * @dependency {Object} auth - The authentication state containing user information
 * @dependency {Object} prevUser - The previous user state for comparison
 */
export const useAnalytics = () => {
  const auth = useSelector((store: RootState) => store.auth);
  const prevUser = usePrevious(_get(auth, 'user', {})) || {};
  const location = useLocation();
  const propModal = useSelector((store: RootState) => store.modal);
  const prevPathname = usePrevious(_get(location, 'pathname', ''));
  const prevModalType = usePrevious(_get(propModal, 'modalType', null));
  const alertDetail = useSelector((store: RootState) => store.alertDetail);

  /**
   * Retrieves the SPA experience for analytics.
   * @returns {string} SPA experience type
   */
  const getSpaExperienceForAnalytics = useCallback(() => {
    const marketingDetails = _get(auth, 'user.primaryMember.plan.marketingDetails', {});
    const isDwm = _get(auth, 'user.primaryMember.plan.isDwm', false);
    const isDsp = _get(auth, 'user.primaryMember.plan.isDsp', false);
    const isHometitleStandalone = utils.isHomeTitleStandalone(marketingDetails);
    const isIdAdvisorStandalone = utils.isIdAdvisorStandalone(marketingDetails);

    if (isHometitleStandalone) {
      return 'home title';
    } else if (isIdAdvisorStandalone) {
      return 'id advisor';
    } else if (isDwm) {
      return 'dark web';
    } else if (isDsp) {
      return 'itps';
    } else {
      return '';
    }
  }, [auth]);

  /**
   * Sets up analytics with the current user data
   */
  const setupAnalytics = useCallback(() => {
    if (!window.nortonAnalytics) {
      window.nortonAnalytics = {};
    }
    window.nortonAnalytics.site_country = _get(auth, 'user.countryCode', 'US')?.toLowerCase();
    window.nortonAnalytics.lifelock_product = _get(auth, 'user.primaryMember.plan.packageCode', null);
    window.nortonAnalytics.lifelock_product_name = _get(auth, 'user.primaryMember.plan.name', null);
    window.nortonAnalytics.norton_guid = _get(auth, 'user.primaryMember.nortonGuid', null); // hashed nortonGuid
    window.nortonAnalytics.account_id = _get(auth, 'user.primaryMember.accountGuid', null); // nortonGuid
    window.nortonAnalytics.gen_id = utils.getGenIdFromGdIdCookie();

    const spaExperience = getSpaExperienceForAnalytics();
    const pageNameForAnalytics = _get(location, 'pathname', '');
    if (spaExperience) utils.setPageNameForAnalytics(pageNameForAnalytics, spaExperience);
  }, [auth, location, getSpaExperienceForAnalytics]);

  /**
   * Setup window.analytics for Qualtrics
   */
  useEffect(() => {
    const primaryMember = _get(auth, 'user.primaryMember', null);
    // GUTS-3965 Check accountGuid/nortonGuid (not sure which is reliably present), since accountGuid is gone as of April 2024
    const primaryMemberNortonGuid = _get(auth, 'user.primaryMember.nortonGuid');
    const primaryMemberAccountId = _get(auth, 'user.primaryMember.accountId');
    if (!_isEmpty(primaryMemberNortonGuid) || !_isEmpty(primaryMemberAccountId)) {
      const analytics = {
        sku: _get(primaryMember, 'plan.packageCode'),
        planName: _get(primaryMember, 'plan.name', ''),
        hashedAccountId: _get(primaryMember, 'accountId'),
        promoCode: _get(primaryMember, 'plan.promoCode'),
        isPrimary: true,
        memberGuid: _get(primaryMember, 'accountGuid'),
        primaryMemberGuid: _get(primaryMember, 'accountGuid'),
        userAgent: navigator.userAgent,
        productFamilyId: (_get(primaryMember, 'plan.productFamilyId', '') || '').toString(),
        partnerId: (_get(primaryMember, 'plan.partnerDetails.partnerId', '') || '').toString(),
        partnerGuid: (_get(primaryMember, 'partnerGuid', '') || '').toString(),
      };
      window.analytics = window.analytics || {};
      Object.assign(window.analytics, analytics);
    }
  }, [auth]);

  /**
   * Handles analytics calls on location changes and modal opening.
   */
  useEffect(() => {
    const pathname = _get(location, 'pathname', '');
    const modalType = _get(propModal, 'modalType', null);
    const plan = _get(auth, 'user.primaryMember.plan');

    if (
      !_isEmpty(plan) &&
      ((pathname !== '' && prevPathname !== '/' && pathname !== prevPathname) ||
        (modalType !== null && modalType !== prevModalType))
    ) {
      const spaExperience = getSpaExperienceForAnalytics();
      const pageNameForAnalytics = pathname !== prevPathname ? pathname : modalType;
      if (spaExperience) utils.setPageNameForAnalytics(pageNameForAnalytics, spaExperience);
    }
  }, [auth, location, propModal, prevModalType, prevPathname, getSpaExperienceForAnalytics]);

  /**
   * Handles path changes and alert changes and sets window analytics object to use in Qualtrics feedback data.
   * Sends the current path and alert information to the parent application whenever there's a navigation
   * or when alert details change.
   *
   * @dependency {Object} location - Current router location object
   * @dependency {Object} alertDetail - Alert information from Redux store
   * @dependency {string} prevPathname - Previously visited path for comparison
   */
  useEffect(() => {
    const pathname = _get(location, 'pathname', '');
    const {level2AlertCategory, alertTypeName} = _get(alertDetail, 'alertDetail', {}) || {};
    const itpsDataBag: $TSFixMe = {pathName: '', alertType: '', alertSubtype: ''};

    if ((pathname !== '/' && pathname !== prevPathname) || level2AlertCategory) {
      itpsDataBag.pathName = pathname;

      if (level2AlertCategory) {
        itpsDataBag.alertType = level2AlertCategory;
      }
      if (alertTypeName) {
        itpsDataBag.alertSubtype = alertTypeName;
      }
      utils.sendMessage({type: 'SET_ITPS_DATA_BAG', payload: itpsDataBag}, window.REACT_APP_PARENT_HOST);
    }
  }, [location, alertDetail, prevPathname]);

  /**
   * Monitors changes to the user object and sends updated plan information to the parent window.
   * When the user data changes, this effect extracts the plan name and forwards it to the parent
   * application via the SET_ITPS_DATA_BAG message to ensure consistent analytics tracking.
   *
   * @dependency {Object} auth - The authentication state containing user information
   * @dependency {Object} prevUser - The previous user state for comparison
   */
  useEffect(() => {
    const user = _get(auth, 'user', '');
    if (!_isEmpty(user) && !_isEqual(user, prevUser)) {
      const planName = _get(auth, 'user.primaryMember.plan.name');
      const itpsDataBag = {
        planName,
      };
      utils.sendMessage({type: 'SET_ITPS_DATA_BAG', payload: itpsDataBag}, window.REACT_APP_PARENT_HOST);
    }
  }, [auth, prevUser]);

  return {
    getSpaExperienceForAnalytics,
    setupAnalytics,
  };
};
